import React, {Suspense, useCallback} from 'react';

import {SvgIcon} from '@mui/material';
import {
  AirplanemodeInactive as AirplanemodeInactiveIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  Radar as RadarIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {useSnackbar, withSnackbar} from 'notistack';
import {
  createBrowserRouter,
  createRoutesFromElements,
  LoaderFunctionArgs,
  Navigate,
  Route,
  RouterProvider,
} from 'react-router-dom';
import {useMutation, useQueryClient} from 'react-query';

import API, {Types, PageViewEvent, useAuth, useCsrfToken} from '@wheeebo/polaris-api';
import {
  BreadcrumbItem,
  CircleLoader,
  EmailUnverifiedPage,
  EmailVerifyPage,
  ErrorBoundary,
  ForgotPasswordPage,
  LoadError,
  LoginPage,
  Head,
  PasswordResetPage,
  SignUpPage,
} from '@wheeebo/polaris-ui';
import {DroneIcon} from '@wheeebo/polaris-svg';

import {config} from '@components/Constants';
import AboutPage from '@components/pages/AboutPage';
import EmailUpdatePage from '@components/pages/EmailUpdatePage';
import GuestPageFrame from '@components/pages/GuestPageFrame';
import HomePage from '@components/pages/HomePage';
import MemberPageFrame from '@components/pages/MemberPageFrame';
import MonitorPage from '@components/pages/MonitorPage';
import Monitor3dPage from '@components/pages/Monitor3dPage';
import NoFlyZoneListPage from '@components/pages/NoFlyZoneListPage';
import NoFlyZonePage from '@components/pages/NoFlyZonePage';
import PasswordUpdatePage from '@components/pages/PasswordUpdatePage';
import PrivacyPolicyPage from '@components/pages/PrivacyPolicyPage';
import SettingsPage from '@components/pages/SettingsPage';
import VehiclePage from '@components/pages/VehiclePage';
import VehicleActivatePage from '@components/pages/VehicleActivatePage';
import VehicleListPage from '@components/pages/VehicleListPage';

const AboutBreadcrumb: BreadcrumbItem = {label: 'About', link: '/about', icon: <InfoIcon fontSize="small" />};
const HomeBreadcrumb: BreadcrumbItem = {label: 'Home', link: '/', icon: <HomeIcon fontSize="small" />};
const MonitorBreadcrumb: BreadcrumbItem = {label: 'Monitor', link: '/monitor', icon: <RadarIcon fontSize="small" />};
const NoFlyZoneBreadcrumb: BreadcrumbItem = {
  label: 'No-fly zones',
  link: '/noFlyZones',
  icon: <AirplanemodeInactiveIcon fontSize="small" />,
};
const PrivacyPolicyBreadcrumb: BreadcrumbItem = {
  label: 'Privacy policy',
  link: '/privacyPolicy',
  icon: <SecurityIcon fontSize="small" />,
};
const SettingsBreadcrumb: BreadcrumbItem = {
  label: 'Settings',
  link: '/settings',
  icon: <SettingsIcon fontSize="small" />,
};
const VehicleBreadcrumb: BreadcrumbItem = {
  label: 'Vehicles',
  link: '/vehicles',
  icon: (
    <SvgIcon fontSize="small">
      <DroneIcon />
    </SvgIcon>
  ),
};

function Navigator(): JSX.Element {
  const {csrfToken, error: csrfTokenError} = useCsrfToken();
  const {authState, account, isAccountLoading, signInWithGoogle} = useAuth();
  const {enqueueSnackbar} = useSnackbar();
  const handleGoogleSignIn = useCallback(
    async (token: string): Promise<void> => {
      if (csrfToken === undefined) {
        enqueueSnackbar('サインインの準備中です。少し待ってからもう一度お試しください。', {variant: 'warning'});
        return;
      }
      try {
        await signInWithGoogle(token, csrfToken);
      } catch (e) {
        enqueueSnackbar(e instanceof Error ? e.message : 'Google認証に失敗しました。もう一度お試しください。', {
          variant: 'error',
        });
      }
    },
    [csrfToken, signInWithGoogle, enqueueSnackbar],
  );
  const {mutate: emitPageViewEvent} = useMutation<unknown, unknown, PageViewEvent>((event: PageViewEvent) =>
    API.Track.emitPageViewEvent(csrfToken, event),
  );

  React.useEffect(
    () => emitPageViewEvent({path: location.pathname, search: location.search, hash: location.hash}),
    [emitPageViewEvent],
  );

  const locationState = React.useMemo(
    () => ({pathname: location.pathname, search: location.search, hash: location.hash}),
    [],
  );

  const queryClient = useQueryClient();
  const fetchNoFlyZone = async ({params}: LoaderFunctionArgs): Promise<Types.NoFlyZone> =>
    await queryClient.fetchQuery(params.noFlyZoneId ? API.NoFlyZones.getKey(params.noFlyZoneId) : '', () =>
      params.noFlyZoneId ? API.NoFlyZones.get(params.noFlyZoneId) : undefined,
    );
  const fetchVehicle = async ({params}: LoaderFunctionArgs): Promise<Types.Vehicle> =>
    await queryClient.fetchQuery(params.vehicleId ? API.Vehicles.getKey(params.vehicleId, {}) : '', () =>
      params.vehicleId ? API.Vehicles.get(params.vehicleId, {}) : undefined,
    );
  const guestRoute = createRoutesFromElements(
    <Route errorElement={<ErrorBoundary />}>
      <Route path="/" element={<GuestPageFrame />}>
        <Route index element={<LoginPage googleClientId={config.GOOGLE_CLIENT_ID} onGoogleSignIn={handleGoogleSignIn} />} />
        <Route path="signup" element={<SignUpPage googleClientId={config.GOOGLE_CLIENT_ID} onGoogleSignUp={handleGoogleSignIn} />} />
        <Route path="forgot_password" element={<ForgotPasswordPage />} />
        <Route path="password_reset" element={<PasswordResetPage />} />
        <Route path="email_unverified" element={<EmailUnverifiedPage />} />
        <Route path="email_verify" element={<EmailVerifyPage />} />
        <Route path="*" element={<Navigate to="/" state={{from: locationState}} />} />
      </Route>
      <Route path="*" element={<Navigate to="/" state={{from: locationState}} />} />
    </Route>,
  );
  const EmailUnverifiedRoute = createRoutesFromElements(
    <Route errorElement={<ErrorBoundary />}>
      <Route path="/" element={<GuestPageFrame />}>
        <Route index element={<Navigate to="/email_unverified" state={{from: locationState}} />} />
        <Route path="email_unverified" element={<EmailUnverifiedPage />} />
        <Route path="email_verify" element={<EmailVerifyPage />} />
        <Route path="*" element={<Navigate to="/" state={{from: locationState}} />} />
      </Route>
      <Route path="*" element={<Navigate to="/" state={{from: locationState}} />} />
    </Route>,
  );
  const memberRoute = createRoutesFromElements(
    <Route errorElement={<ErrorBoundary />}>
      <Route path="/" element={<MemberPageFrame />}>
        <Route index element={<HomePage />} handle={{crumb: () => [HomeBreadcrumb]}} />
        <Route path="about" element={<AboutPage />} handle={{crumb: () => [AboutBreadcrumb]}} />
        <Route path="accounts">
          <Route index element={<Navigate to="/" />} />
        </Route>
        <Route path="monitor" element={<MonitorPage />} handle={{crumb: () => [MonitorBreadcrumb]}} />
        <Route path="monitor3d" element={<Monitor3dPage />} handle={{crumb: () => [MonitorBreadcrumb]}} />
        <Route path="noFlyZones">
          <Route index element={<NoFlyZoneListPage />} handle={{crumb: () => [NoFlyZoneBreadcrumb]}} />
          <Route
            path=":noFlyZoneId"
            element={<NoFlyZonePage />}
            loader={fetchNoFlyZone}
            handle={{crumb: (data: Types.NoFlyZone) => [NoFlyZoneBreadcrumb, {label: data?.name || ' - '}]}}
          />
        </Route>
        <Route path="privacyPolicy" element={<PrivacyPolicyPage />} handle={{crumb: () => [PrivacyPolicyBreadcrumb]}} />
        <Route path="settings">
          <Route index element={<SettingsPage />} handle={{crumb: () => [SettingsBreadcrumb]}} />
          <Route
            path="update_email"
            element={<EmailUpdatePage />}
            handle={{crumb: () => [SettingsBreadcrumb, {label: 'Email update'}]}}
          />
          <Route
            path="update_password"
            element={<PasswordUpdatePage />}
            handle={{crumb: () => [SettingsBreadcrumb, {label: 'Password update'}]}}
          />
        </Route>
        <Route path="vehicles">
          <Route index element={<VehicleListPage />} handle={{crumb: () => [VehicleBreadcrumb]}} />
          <Route
            path=":vehicleId"
            element={<VehiclePage />}
            loader={fetchVehicle}
            handle={{crumb: (data: Types.Vehicle) => [VehicleBreadcrumb, {label: data?.name || ' - '}]}}
          />
          <Route
            path=":vehicleId/activate"
            element={<VehicleActivatePage />}
            loader={fetchVehicle}
            handle={{
              crumb: (data: Types.Vehicle) => [VehicleBreadcrumb, {label: data?.name || ' - '}, {label: 'Activation'}],
            }}
          />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="" state={{from: locationState}} />} />
    </Route>,
  );
  return (
    <Suspense fallback={<CircleLoader />}>
      <Head />
      {csrfTokenError ? (
        <LoadError />
      ) : authState.isLoading || isAccountLoading ? (
        <CircleLoader />
      ) : !account ? (
        <RouterProvider router={createBrowserRouter(guestRoute)} />
      ) : !account.emailVerified ? (
        <RouterProvider router={createBrowserRouter(EmailUnverifiedRoute)} />
      ) : (
        <RouterProvider router={createBrowserRouter(memberRoute)} />
      )}
    </Suspense>
  );
}

export default React.memo(withSnackbar(Navigator));
